import { isIframe, isWebView } from './env'

/**
 * QSC → Host (outbound) 메시지 타입
 *
 * 시나리오:
 *  1. oms_web 안에 임베드된 경우 → READY, LOGOUT, SESSION_EXPIRED, NAVIGATE
 *  2. 제3의 앱 안에 임베드된 상태에서 oms_web으로 전환 요청 → SWITCH_TO_OMS
 */
export type OutboundMessage =
  | { type: 'READY'; payload?: { navigationReady: boolean } }
  | { type: 'LOGOUT' }
  | { type: 'SESSION_EXPIRED' }
  | { type: 'REQUEST_TOKEN_REFRESH' }
  | { type: 'CLOSE_WEBVIEW' }
  | { type: 'GO_BACK' }
  | { type: 'NAVIGATE'; payload: { path: string; title?: string } }
  | { type: 'SWITCH_TO_OMS'; payload?: { path?: string } }
  | { type: 'REQUEST_LOCATION'; payload?: { requestId?: string } }
  | { type: 'SET_ORIENTATION'; payload: { orientation: 'landscape' | 'portrait' } }
  | {
      type: 'REQUEST_BIOMETRIC_LOGIN'
      payload: { accountKey: string; corpCd: string; userId: string }
    }

/**
 * Host → QSC (inbound) 메시지 타입
 */
export type InboundMessage =
  | { type: 'SET_TOKEN'; payload: { accessToken: string } }
  | { type: 'TOKEN_REFRESH_FAILED' }
  | { type: 'GO_BACK' }
  | { type: 'NAVIGATE'; payload: { path: string } }
  | { type: 'SET_CONTEXT'; payload: Record<string, unknown> }
  | {
      type: 'LOCATION_RESULT'
      payload: {
        success: boolean
        requestId?: string
        latitude?: number
        longitude?: number
        accuracy?: number | null
        message?: string
      }
    }
  | {
      type: 'BIOMETRIC_LOGIN_RESULT'
      payload: {
        success: boolean
        corpCd?: string
        userId?: string
        password?: string
        message?: string
      }
    }

const OMS_PARENT_ORIGINS = [
  'https://dev-oms.bizbee.ne.kr',
  'https://oms-app.bizbee.co.kr',
] as const

const ALLOWED_PARENT_ORIGINS: readonly string[] = [...OMS_PARENT_ORIGINS]

const isLocalDevelopmentOrigin = (origin: string) => {
  if (!import.meta.env.DEV) return false

  try {
    const { hostname } = new URL(origin)
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true
    if (hostname.startsWith('192.168.')) return true
    if (hostname.startsWith('10.')) return true

    const match = hostname.match(/^172\.(\d{1,2})\./)
    if (!match) return false

    const secondOctet = Number(match[1])
    return secondOctet >= 16 && secondOctet <= 31
  } catch {
    return false
  }
}

const isAllowedParentOrigin = (origin: string) =>
  ALLOWED_PARENT_ORIGINS.includes(origin) || isLocalDevelopmentOrigin(origin)

export const postToHost = (msg: OutboundMessage): void => {
  if (isIframe()) {
    if (import.meta.env.DEV) {
      window.parent.postMessage(msg, '*')
      return
    }

    for (const origin of ALLOWED_PARENT_ORIGINS) {
      try {
        window.parent.postMessage(msg, origin)
      } catch {
        // Ignore unavailable parent windows or rejected target origins.
      }
    }
    return
  }

  if (isWebView()) {
    ;(
      window as unknown as {
        ReactNativeWebView: { postMessage: (msg: string) => void }
      }
    ).ReactNativeWebView.postMessage(JSON.stringify(msg))
  }
}

export const listenFromHost = (handler: (msg: InboundMessage) => void): (() => void) => {
  const listener = (event: MessageEvent) => {
    if (isIframe()) {
      if (!isAllowedParentOrigin(event.origin)) return
    }
    try {
      const data =
        typeof event.data === 'string'
          ? (JSON.parse(event.data) as InboundMessage)
          : (event.data as InboundMessage)
      if (data?.type) handler(data)
    } catch {
      // Ignore malformed host messages.
    }
  }

  window.addEventListener('message', listener)
  document.addEventListener('message', listener as EventListener)
  return () => {
    window.removeEventListener('message', listener)
    document.removeEventListener('message', listener as EventListener)
  }
}
